import { ReactElement, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { TextField } from '../../../components/TextField';
import { SelectField } from '../../../components/SelectField/SelectField';
import { Button } from '../../../components/Button/Button';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import { Routes } from '../../../enums/Routes';
import { useCreateLead } from '../../../hooks/leads/useLeads';
import { useGetPartnerDashboardQuery } from '../../../hooks/queries/useGetPartnerDashboardQuery';
import { formatNOK } from '../../../utils/currencyFormatters';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { WalletIcon } from '@navikt/aksel-icons';
import { parseOptionalNumber, convertNOKToOre, calculateCashbackEstimate } from '../../../utils/pricing';
import classes from './CreateLeadPage.module.css';

const makeCreateLeadSchema = (t: any) => z.object({
  companyName: z.string().min(1, t('partnerAdmin.createLead.validation.companyNameRequired')),
  contactPersonName: z.string().optional(),
  contactEmail: z.string().email(t('partnerAdmin.createLead.validation.invalidEmail')).optional().or(z.literal('')),
  contactPhone: z.string().optional(),
  source: z.string().optional(),
  quality: z.enum(['', 'HOT', 'WARM', 'COLD']).optional(),
  estimatedSeats: z.string().optional(),
  estimatedPricePerSeat: z.string().optional(),
  estimatedBillingPeriod: z.enum(['', 'monthly', 'yearly']).optional(),
  nextFollowUpDate: z.string().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<ReturnType<typeof makeCreateLeadSchema>>;
type FormErrors = Partial<Record<keyof FormData, string>>;

export const CreateLeadPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createLeadMutation = useCreateLead();
  const { data: dashboardData } = useGetPartnerDashboardQuery();
  const createLeadSchema = makeCreateLeadSchema(t);

  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    contactPersonName: '',
    contactEmail: '',
    contactPhone: '',
    source: '',
    quality: '',
    estimatedSeats: '',
    estimatedPricePerSeat: '',
    estimatedBillingPeriod: '',
    nextFollowUpDate: '',
    notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const cashbackEstimate = useMemo(
    () =>
      calculateCashbackEstimate(
        parseOptionalNumber(formData.estimatedPricePerSeat || ''),
        parseOptionalNumber(formData.estimatedSeats || ''),
        formData.estimatedBillingPeriod || null,
        dashboardData?.partnerInfo.cashbackPercentage ?? null,
      ),
    [formData.estimatedPricePerSeat, formData.estimatedSeats, formData.estimatedBillingPeriod, dashboardData],
  );

  const updateField = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;

    if (field === 'estimatedSeats' && value && !/^\d+$/.test(value)) return;
    if (field === 'estimatedPricePerSeat' && value && !/^\d+(\.\d{0,2})?$/.test(value)) return;

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (createLeadMutation.isPending) {
      return;
    }

    // Validate schema
    const result = createLeadSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof FormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const pricePerSeatNOK = parseOptionalNumber(formData.estimatedPricePerSeat || '');
    const pricePerSeatOre = pricePerSeatNOK !== null ? convertNOKToOre(pricePerSeatNOK) : undefined;

    // Build payload with only defined values
    const payload: any = {
      companyName: formData.companyName.trim(),
    };

    if (formData.contactPersonName?.trim()) payload.contactPersonName = formData.contactPersonName.trim();
    if (formData.contactEmail?.trim()) payload.contactEmail = formData.contactEmail.trim();
    if (formData.contactPhone?.trim()) payload.contactPhone = formData.contactPhone.trim();
    if (formData.source?.trim()) payload.source = formData.source.trim();
    if (formData.quality && formData.quality !== '') payload.quality = formData.quality;
    if (parseOptionalNumber(formData.estimatedSeats || '') !== null) {
      payload.estimatedSeats = parseOptionalNumber(formData.estimatedSeats || '');
    }
    if (pricePerSeatOre !== undefined) payload.estimatedPricePerSeat = pricePerSeatOre;
    if (formData.estimatedBillingPeriod && formData.estimatedBillingPeriod !== '') {
      payload.estimatedBillingPeriod = formData.estimatedBillingPeriod;
    }
    if (formData.nextFollowUpDate?.trim()) {
      payload.nextFollowUpDate = new Date(formData.nextFollowUpDate).toISOString();
    }
    if (formData.notes?.trim()) payload.notes = formData.notes.trim();

    createLeadMutation.mutate(
      payload,
      {
        onSuccess: () => {
          toast.success(t('partnerAdmin.createLead.toast.createSuccess'));
          navigate(Routes.PartnerAdminLeads);
        },
        onError: () => {
          toast.error(t('partnerAdmin.createLead.toast.createError'));
        },
      }
    );
  };

  const monthlyRevenue = parseOptionalNumber(formData.estimatedPricePerSeat) && parseOptionalNumber(formData.estimatedSeats)
    ? parseOptionalNumber(formData.estimatedPricePerSeat)! * parseOptionalNumber(formData.estimatedSeats)!
    : null;

  return (
    <FocusPageLayout
      title={t('partnerAdmin.createLead.pageTitle')}
      backLink={Routes.PartnerAdminLeads}
      backLinkLabel={t('partnerAdmin.createLead.backToLeads')}
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('partnerAdmin.editLead.sections.companyInfo')}
          </Paragraph>

          <TextField
            name="companyName"
            label={t('partnerAdmin.editLead.fields.companyName.label')}
            value={formData.companyName}
            onChange={updateField('companyName')}
            placeholder={t('partnerAdmin.editLead.fields.companyName.placeholder')}
            type="text"
            required
            errorMessage={errors.companyName}
          />

          <TextField
            name="source"
            label={t('partnerAdmin.editLead.fields.source.label')}
            value={formData.source}
            onChange={updateField('source')}
            placeholder={t('partnerAdmin.editLead.fields.source.placeholder')}
            type="text"
            errorMessage={errors.source}
          />

          <SelectField
            name="quality"
            label={t('partnerAdmin.editLead.fields.leadQuality.label')}
            value={formData.quality}
            onChange={updateField('quality')}
            options={[
              { label: t('partnerAdmin.editLead.fields.leadQuality.options.hot'), value: 'HOT' },
              { label: t('partnerAdmin.editLead.fields.leadQuality.options.warm'), value: 'WARM' },
              { label: t('partnerAdmin.editLead.fields.leadQuality.options.cold'), value: 'COLD' },
            ]}
            placeholder={t('partnerAdmin.editLead.fields.leadQuality.placeholder')}
            errorMessage={errors.quality}
          />
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('partnerAdmin.editLead.sections.contactPerson')}
          </Paragraph>

          <TextField
            name="contactPersonName"
            label={t('partnerAdmin.editLead.fields.contactName.label')}
            value={formData.contactPersonName}
            onChange={updateField('contactPersonName')}
            placeholder={t('partnerAdmin.editLead.fields.contactName.placeholder')}
            type="text"
            errorMessage={errors.contactPersonName}
          />

          <div className={classes.nameFields}>
            <TextField
              name="contactEmail"
              label={t('partnerAdmin.editLead.fields.contactEmail.label')}
              value={formData.contactEmail}
              onChange={updateField('contactEmail')}
              placeholder={t('partnerAdmin.editLead.fields.contactEmail.placeholder')}
              type="email"
              errorMessage={errors.contactEmail}
            />

            <TextField
              name="contactPhone"
              label={t('partnerAdmin.editLead.fields.contactPhone.label')}
              value={formData.contactPhone}
              onChange={updateField('contactPhone')}
              placeholder={t('partnerAdmin.editLead.fields.contactPhone.placeholder')}
              type="text"
              errorMessage={errors.contactPhone}
            />
          </div>
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('partnerAdmin.editLead.sections.estimatedValue')}
          </Paragraph>
          <Paragraph size="sm" className={classes.sectionDescription}>
            {t('partnerAdmin.editLead.estimatedValueDescription')}
          </Paragraph>

          <TextField
            name="estimatedSeats"
            label={t('partnerAdmin.editLead.fields.estimatedSeats.label')}
            value={formData.estimatedSeats}
            onChange={updateField('estimatedSeats')}
            placeholder={t('partnerAdmin.editLead.fields.estimatedSeats.placeholder')}
            type="text"
            errorMessage={errors.estimatedSeats}
          />

          <TextField
            name="estimatedPricePerSeat"
            label={t('partnerAdmin.editLead.fields.pricePerSeat.label')}
            value={formData.estimatedPricePerSeat}
            onChange={updateField('estimatedPricePerSeat')}
            placeholder={t('partnerAdmin.editLead.fields.pricePerSeat.placeholder')}
            type="text"
            errorMessage={errors.estimatedPricePerSeat}
          />

          <SelectField
            name="estimatedBillingPeriod"
            label={t('partnerAdmin.editLead.fields.billingPeriod.label')}
            value={formData.estimatedBillingPeriod}
            onChange={updateField('estimatedBillingPeriod')}
            options={[
              { label: t('partnerAdmin.editLead.fields.billingPeriod.options.monthly'), value: 'monthly' },
              { label: t('partnerAdmin.editLead.fields.billingPeriod.options.yearly'), value: 'yearly' },
            ]}
            placeholder={t('partnerAdmin.editLead.fields.billingPeriod.placeholder')}
            errorMessage={errors.estimatedBillingPeriod}
          />

          {cashbackEstimate && (
            <ContainerCard>
              <ContainerCard.Title icon={<WalletIcon />}>
                {t('partnerAdmin.editLead.pipelineValue.title')}
              </ContainerCard.Title>
              <ContainerCard.Body>
                <Paragraph size="sm" style={{ marginBottom: '1rem', color: 'var(--a-text-subtle)' }}>
                  {t('partnerAdmin.editLead.pipelineValue.description')}
                </Paragraph>
                <div className={classes.cashbackBreakdown}>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>{t('partnerAdmin.editLead.pipelineValue.pricePerSeat')}</span>
                    <span className={classes.breakdownValue}>
                      {formData.estimatedPricePerSeat ? `${formData.estimatedPricePerSeat} kr` : '–'}
                    </span>
                  </div>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>{t('partnerAdmin.editLead.pipelineValue.numberOfSeats')}</span>
                    <span className={classes.breakdownValue}>
                      {formData.estimatedSeats || '–'}
                    </span>
                  </div>

                  <div className={classes.breakdownDivider} />

                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}><strong>{t('partnerAdmin.editLead.pipelineValue.monthlyValue')}</strong></span>
                    <span className={classes.breakdownValue}>
                      <strong>{monthlyRevenue ? formatNOK(monthlyRevenue) : '–'}</strong>
                    </span>
                  </div>

                  <div className={classes.breakdownHighlight}>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>{t('partnerAdmin.editLead.pipelineValue.estimatedMonthlyCashback')}</span>
                      <span className={classes.breakdownValue}>{formatNOK(cashbackEstimate.monthly)}</span>
                    </div>
                  </div>

                  <div className={classes.breakdownHighlight}>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>{t('partnerAdmin.editLead.pipelineValue.estimatedYearlyCashback')}</span>
                      <span className={classes.breakdownValue}>{formatNOK(cashbackEstimate.yearly)}</span>
                    </div>
                  </div>
                </div>
              </ContainerCard.Body>
            </ContainerCard>
          )}
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            {t('partnerAdmin.editLead.sections.followUp')}
          </Paragraph>

          <TextField
            name="nextFollowUpDate"
            label={t('partnerAdmin.editLead.fields.nextFollowUpDate.label')}
            value={formData.nextFollowUpDate}
            onChange={updateField('nextFollowUpDate')}
            type="date"
            errorMessage={errors.nextFollowUpDate}
          />

          <div>
            <label htmlFor="notes" className={classes.notesLabel}>
              {t('partnerAdmin.editLead.fields.notes.label')}
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={updateField('notes')}
              placeholder={t('partnerAdmin.editLead.fields.notes.placeholder')}
              className={classes.notesField}
            />
            {errors.notes && <span className={classes.errorMessage}>{errors.notes}</span>}
          </div>
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            size="medium"
            onClick={() => navigate(Routes.PartnerAdminLeads)}
            disabled={createLeadMutation.isPending}
            type="button"
          >
            {t('partnerAdmin.createLead.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            size="medium"
            type="submit"
            disabled={createLeadMutation.isPending}
          >
            {t('partnerAdmin.createLead.actions.create')}
          </Button>
        </div>
      </form>
    </FocusPageLayout>
  );
};
